import React, { useState } from 'react'
import { useI18n } from '../../../i18n'
import { useAuth } from '../../AuthProvider'
import { Button } from '../../ui/Button'
import { useNavigate } from 'react-router-dom'
import listStyles from '../../../styles/pages/governance/GovernanceListing.module.scss'
import type { ControlCentricCompletionSummary as SummaryType } from '../../../services/controlCentricWorkflowApi'
import {
  continueRemediationSession,
  reopenAssessmentSession,
  startNewAssessmentCycle,
  fetchUnresolvedControls,
} from '../../../services/complianceLifecycleApi'
import { buildRemediationJourneyUrl } from '../../../pages/compliance-center/journeyRemediationNav'
import { featureFlags } from '../../../config/featureFlags'

type Props = {
  summary: SummaryType
  standardCode: string
  sessionId?: string
  onSwitchSession?: () => void
  onLifecycleAction?: () => void
}

export function ControlCentricCompletionSummary({
  summary,
  standardCode,
  sessionId,
  onSwitchSession,
  onLifecycleAction,
}: Props) {
  const { t } = useI18n()
  const { token } = useAuth()
  const navigate = useNavigate()
  const [actionError, setActionError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const repo = summary.evidenceRepositorySummary
  const confirmedPct =
    summary.controlsTotal > 0
      ? Math.round((summary.controlsEvidenceSupported / summary.controlsTotal) * 100)
      : 0
  const phase15 = featureFlags.CONTINUOUS_COMPLIANCE_OPERATIONS_V1

  const goRemediation = (controlKey?: string | null) => {
    if (!sessionId) return
    navigate(
      buildRemediationJourneyUrl({
        sessionId,
        standardCode,
        controlKey: controlKey || undefined,
        mode: 'remediation',
      }),
    )
  }

  const runLifecycle = async (action: 'review' | 'continue' | 'reopen' | 'newCycle') => {
    if (!token || !sessionId) return
    setBusy(true)
    setActionError(null)
    try {
      if (action === 'continue') {
        const data = await continueRemediationSession(token, sessionId)
        goRemediation(data?.resumeControlKey || data?.unresolvedControls?.resumeControlKey || null)
      } else if (action === 'review') {
        await continueRemediationSession(token, sessionId)
        const unresolved = await fetchUnresolvedControls(token, sessionId)
        const firstFailed =
          unresolved?.controls?.find((c) => c.status === 'FAILED')?.controlKey ||
          unresolved?.resumeControlKey ||
          unresolved?.controls?.[0]?.controlKey
        goRemediation(firstFailed)
      } else if (action === 'reopen') {
        await reopenAssessmentSession(token, sessionId)
        const unresolved = await fetchUnresolvedControls(token, sessionId)
        goRemediation(unresolved?.resumeControlKey || unresolved?.controls?.[0]?.controlKey || null)
      } else if (action === 'newCycle') {
        const next = await startNewAssessmentCycle(token, sessionId)
        navigate(buildRemediationJourneyUrl({ sessionId: next.sessionId, standardCode, mode: 'assessment' }))
      }
      onLifecycleAction?.()
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Action failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className={`${listStyles.panel} space-y-4`}>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          {t('controlCentric.completion.eyebrow', 'Operational Compliance Journey')}
        </p>
        <h3 className="text-lg font-semibold text-slate-900">
          {t('controlCentric.completion.title', '{{standard}} assessment completed', {
            standard: standardCode,
          })}
        </h3>
        {summary.subtitle ? <p className="mt-1 text-sm text-amber-800">{summary.subtitle}</p> : null}
      </div>

      <div className={`${listStyles.metricsGrid} ${listStyles.metricsGrid5}`}>
        <div className={`${listStyles.metricTile} ${listStyles.metricTileGreen}`}>
          <p className={listStyles.metricTileLabel}>
            {t('controlCentric.metrics.controlsCompleted', 'Controls completed')}
          </p>
          <p className={`${listStyles.metricTileValue} ${listStyles.textGreen}`}>
            {summary.controlsCompleted}/{summary.controlsTotal}
          </p>
        </div>
        <div className={`${listStyles.metricTile} ${listStyles.metricTileBlue}`}>
          <p className={listStyles.metricTileLabel}>
            {t('controlCentric.metrics.evidenceSupported', 'Evidence-supported controls')}
          </p>
          <p className={`${listStyles.metricTileValue} ${listStyles.textBlue}`}>
            {summary.controlsEvidenceSupported}/{summary.controlsTotal}
          </p>
        </div>
        <div className={`${listStyles.metricTile} ${listStyles.metricTileYellow}`}>
          <p className={listStyles.metricTileLabel}>
            {t('controlCentric.metrics.controlsWithGaps', 'Controls with gaps')}
          </p>
          <p className={`${listStyles.metricTileValue} ${listStyles.textYellow}`}>
            {summary.controlsWithGaps}/{summary.controlsTotal}
          </p>
        </div>
        <div className={`${listStyles.metricTile} ${listStyles.metricTileBlue}`}>
          <p className={listStyles.metricTileLabel}>
            {t('controlCentric.metrics.confirmedCompliance', 'Confirmed compliance')}
          </p>
          <p className={`${listStyles.metricTileValue} ${listStyles.textBlue}`}>{confirmedPct}%</p>
        </div>
      </div>

      {repo ? (
        <p className="text-sm text-slate-600">
          {t('controlCentric.completion.evidenceRepo', 'Evidence repository')}:{' '}
          <span className="font-medium text-slate-900">{repo.totalArtifacts}</span>{' '}
          {t('controlCentric.metrics.evidenceArtifacts', 'artifacts')}
        </p>
      ) : null}

      {phase15 && sessionId ? (
        <div className="space-y-3">
          <div className={listStyles.actionsGap}>
            <Button
              type="button"
              size="small"
              color="primary"
              disabled={busy}
              onClick={() => void runLifecycle('continue')}
            >
              {t('controlCentric.completion.continueRemediation', 'Continue remediation')}
            </Button>
            <Button
              type="button"
              size="small"
              color="outline"
              disabled={busy}
              onClick={() => void runLifecycle('review')}
            >
              {t('controlCentric.completion.reviewFailed', 'Review failed controls')}
            </Button>
          </div>
          <div className={listStyles.actionsGap}>
            <Button type="button" size="small" color="outline" onClick={() => navigate('/compliance-center/certification')}>
              {t('controlCentric.completion.openCertification', 'Open certification workspace')}
            </Button>
            <Button type="button" size="small" color="outline" onClick={() => navigate('/compliance-center/operations')}>
              {t('controlCentric.completion.openOperations', 'Compliance operations')}
            </Button>
            <Button type="button" size="small" color="outline" onClick={() => navigate('/compliance-center')}>
              {t('controlCentric.completion.backToCenter', 'Back to Compliance Center')}
            </Button>
          </div>
          <div className={`border-t border-slate-100 pt-3 ${listStyles.actionsGap}`}>
            <Button
              type="button"
              size="small"
              color="outline"
              disabled={busy}
              onClick={() => void runLifecycle('reopen')}
            >
              {t('controlCentric.completion.reopenAssessment', 'Reopen assessment')}
            </Button>
            <Button
              type="button"
              size="small"
              color="outline"
              disabled={busy}
              onClick={() => void runLifecycle('newCycle')}
            >
              {t('controlCentric.completion.newCycle', 'Start new cycle')}
            </Button>
            {onSwitchSession ? (
              <Button type="button" size="small" color="outline" onClick={onSwitchSession}>
                {t('controlCentric.session.switch', 'Switch session')}
              </Button>
            ) : null}
          </div>
        </div>
      ) : (
        <div className={listStyles.actionsGap}>
          <Button type="button" size="small" color="primary" onClick={() => navigate('/compliance-center/certification')}>
            {t('controlCentric.completion.openCertification', 'Open certification workspace')}
          </Button>
          <Button type="button" size="small" color="outline" onClick={() => navigate('/compliance-center')}>
            {t('controlCentric.completion.backToCenter', 'Back to Compliance Center')}
          </Button>
        </div>
      )}

      {actionError ? <p className="text-sm text-red-600">{actionError}</p> : null}
    </section>
  )
}
